import React, { useEffect, useMemo, useState } from 'react';
import { 
  Box, Typography, Button, Paper, Container,
  Tabs, Tab, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  FormControl, InputLabel, Select, MenuItem, IconButton
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

interface Transaction {
  id: number;
  description: string;
  amount: number;
  type: 'income' | 'expense';
  date: string;
  currency?: string;
}

const HomePage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState<string>('VND');
  const [uploading, setUploading] = useState(false);
  const [receiptResult, setReceiptResult] = useState<any>(null);
  const [rates, setRates] = useState<Record<string, number> | null>(null);
  const [ratesFetchedAt, setRatesFetchedAt] = useState<number | null>(null);
  const [convertingId, setConvertingId] = useState<number | null>(null);
  const [targetCurrency, setTargetCurrency] = useState<string>('VND');
  const [convertError, setConvertError] = useState<string | null>(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('bt_transactions');
      if (saved) {
        const parsed: Transaction[] = JSON.parse(saved);
        // Backfill currency for older entries
        const withCurrency = parsed.map(t => ({ ...t, currency: t.currency || 'VND' }));
        setTransactions(withCurrency);
      }
    } catch {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('bt_transactions', JSON.stringify(transactions));
    } catch {}
  }, [transactions]);

  const balance = useMemo(() => transactions
    .filter(t => (t.currency || 'VND') === selectedCurrency)
    .reduce((acc, t) => acc + (t.type === 'income' ? t.amount : -t.amount), 0), [transactions, selectedCurrency]);

  const LOCALES: Record<string, string> = {
    VND: 'vi-VN',
    USD: 'en-US',
    EUR: 'de-DE',
    GBP: 'en-GB',
    JPY: 'ja-JP',
    KRW: 'ko-KR',
    THB: 'th-TH',
    INR: 'en-IN',
  };

  const deleteTx = (id: number) => {
    setTransactions(prev => prev.filter(t => t.id !== id));
  };
  const CURRENCY_OPTIONS = Object.keys(LOCALES);
  const formatAmount = (code: string, n: number) => new Intl.NumberFormat(LOCALES[code] || 'en-US', {
    style: 'currency',
    currency: code,
    maximumFractionDigits: code === 'VND' || code === 'JPY' || code === 'KRW' ? 0 : 2,
  }).format(Math.abs(n));

  const ensureRates = async () => {
    const isStale = !ratesFetchedAt || (Date.now() - ratesFetchedAt) > 60 * 60 * 1000; // 1h
    if (rates && !isStale) return rates;
    const API_KEY = process.env.REACT_APP_FREECURRENCY_KEY || 'fca_live_RNX1zqAKNNDjXe35iVw02dtRWZxAcxmoCqjE9iQq';
    const codes = CURRENCY_OPTIONS.map(c => c.toUpperCase()).join(',');
    // Note: Free plan may reject `base_currency` param; default base is USD
    const url = `https://api.freecurrencyapi.com/v1/latest?apikey=${API_KEY}&currencies=${codes}`;
    const res = await fetch(url);
    const json = await res.json();
    if (json && json.data) {
      setRates(json.data as Record<string, number>);
      setRatesFetchedAt(Date.now());
      return json.data as Record<string, number>;
    }
    const msg = json && json.message ? json.message : 'Unable to fetch currency rates';
    throw new Error(`${msg}${res && res.status ? ` (status ${res.status})` : ''}`);
  };

  const convertAmount = async (amount: number, from: string, to: string) => {
    if (from === to) return amount;
    const r = await ensureRates();
    const f = (from || 'VND').toUpperCase();
    const t = (to || 'VND').toUpperCase();
    const rateFrom = f === 'USD' ? 1 : r[f];
    const rateTo = t === 'USD' ? 1 : r[t];
    if (!rateFrom || !rateTo) throw new Error('Missing rate');
    const inUSD = amount / rateFrom; // because rates are relative to USD
    let converted = inUSD * rateTo;
    // Round for zero-decimal currencies (VND, JPY, KRW)
    if (t === 'VND' || t === 'JPY' || t === 'KRW') converted = Math.round(converted);
    return converted;
  };

  const addTx = (type: 'income' | 'expense') => {
    if (!description || !amount) return;
    const val = parseFloat(amount);
    if (Number.isNaN(val)) return;
    const tx: Transaction = {
      id: Date.now(),
      description,
      amount: val,
      type,
      date: new Date().toLocaleString(),
      currency: selectedCurrency
    };
    setTransactions([tx, ...transactions]);
    setDescription('');
    setAmount('');
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    setReceiptResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const API_BASE = process.env.REACT_APP_API_URL;
      const endpoint = API_BASE ? `${API_BASE}/api/upload-receipt/` : '/api/upload-receipt';
      const res = await fetch(endpoint, {
        method: 'POST',
        body: form
      });
      const data = await res.json();
      setReceiptResult(data);
    } catch (e) {
      setReceiptResult({ error: 'Upload failed' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Budget Tracker
        </Typography>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
          <Tab label="Budget" />
          <Tab label="Upload Receipt" />
          <Tab label="Expenses" />
        </Tabs>

        {tab === 0 && (
          <Box>
            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Balance ({selectedCurrency}): {formatAmount(selectedCurrency, balance)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} size="small" />
                <TextField label="Amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} size="small" />
                <Button variant="contained" color="success" onClick={() => addTx('income')}>Add Income</Button>
                <Button variant="contained" color="error" onClick={() => addTx('expense')}>Add Expense</Button>
                
              </Box>
            </Paper>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Currency</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.length === 0 ? (
                    <TableRow><TableCell colSpan={6} align="center">No transactions</TableCell></TableRow>
                  ) : (
                    transactions.map(t => (
                      <TableRow key={t.id}>
                        <TableCell>{t.date}</TableCell>
                        <TableCell>{t.description}</TableCell>
                        <TableCell align="right" sx={{ color: t.type==='income'?'success.main':'error.main', fontWeight: 'bold' }}>
                          {t.type==='income' ? '+' : '-'}{formatAmount(t.currency || 'VND', t.amount)}
                        </TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{t.type}</TableCell>
                        <TableCell>{t.currency || 'VND'}</TableCell>
                        <TableCell align="center">
                          <IconButton aria-label="delete" color="error" onClick={() => deleteTx(t.id)} size="small">
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                          {convertingId === t.id ? (
                            <Box sx={{ display: 'inline-flex', gap: 1, ml: 1 }}>
                              <FormControl size="small" sx={{ minWidth: 110 }}>
                                <InputLabel id={`to-${t.id}`}>To</InputLabel>
                                <Select labelId={`to-${t.id}`} label="To" value={targetCurrency} onChange={(e) => setTargetCurrency(e.target.value)}>
                                  {CURRENCY_OPTIONS.map(code => (
                                    <MenuItem key={code} value={code}>{code}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <Button size="small" variant="outlined" onClick={async () => {
                                setConvertError(null);
                                try {
                                  const from = t.currency || 'VND';
                                  const to = targetCurrency;
                                  const newAmount = await convertAmount(t.amount, from, to);
                                  setTransactions(prev => prev.map(x => x.id === t.id ? { ...x, amount: newAmount, currency: to } : x));
                                } catch (e: any) {
                                  setConvertError('Conversion failed');
                                } finally {
                                  setConvertingId(null);
                                }
                              }}>Convert</Button>
                              <Button size="small" onClick={() => setConvertingId(null)}>Cancel</Button>
                              {convertError && <Typography variant="caption" color="error">{convertError}</Typography>}
                            </Box>
                          ) : (
                            <Button size="small" sx={{ ml: 1 }} onClick={() => { setConvertingId(t.id); setTargetCurrency(selectedCurrency); }}>Convert</Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {tab === 1 && (
          <Paper elevation={3} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button variant="contained" component="label" disabled={uploading}>
                {uploading ? 'Uploading...' : 'Select Receipt'}
                <input hidden type="file" accept="image/*" onChange={(e) => e.target.files && handleUpload(e.target.files[0])} />
              </Button>
            </Box>
            {receiptResult && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1">Result</Typography>
                <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(receiptResult, null, 2)}</pre>
              </Box>
            )}
          </Paper>
        )}

        {tab === 2 && (
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>All Expenses</Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Currency</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.filter(t=>t.type==='expense').map(t => (
                    <TableRow key={t.id}>
                      <TableCell>{t.date}</TableCell>
                      <TableCell>{t.description}</TableCell>
                      <TableCell align="right">-{formatAmount(t.currency || 'VND', t.amount)}</TableCell>
                      <TableCell>{t.currency || 'VND'}</TableCell>
                      <TableCell align="center">
                        <IconButton aria-label="delete" color="error" onClick={() => deleteTx(t.id)} size="small">
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                        {convertingId === t.id ? (
                          <Box sx={{ display: 'inline-flex', gap: 1, ml: 1 }}>
                            <FormControl size="small" sx={{ minWidth: 110 }}>
                              <InputLabel id={`to-exp-${t.id}`}>To</InputLabel>
                              <Select labelId={`to-exp-${t.id}`} label="To" value={targetCurrency} onChange={(e) => setTargetCurrency(e.target.value)}>
                                {CURRENCY_OPTIONS.map(code => (
                                  <MenuItem key={code} value={code}>{code}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <Button size="small" variant="outlined" onClick={async () => {
                              setConvertError(null);
                              try {
                                const from = t.currency || 'VND';
                                const to = targetCurrency;
                                const newAmount = await convertAmount(t.amount, from, to);
                                setTransactions(prev => prev.map(x => x.id === t.id ? { ...x, amount: newAmount, currency: to } : x));
                              } catch (e: any) {
                                setConvertError('Conversion failed');
                              } finally {
                                setConvertingId(null);
                              }
                            }}>Convert</Button>
                            <Button size="small" onClick={() => setConvertingId(null)}>Cancel</Button>
                            {convertError && <Typography variant="caption" color="error">{convertError}</Typography>}
                          </Box>
                        ) : (
                          <Button size="small" sx={{ ml: 1 }} onClick={() => { setConvertingId(t.id); setTargetCurrency(selectedCurrency); }}>Convert</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default HomePage;

